import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  Modal,
  styled,
  Typography,
  Button,
  DialogActions,
  Fade,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
} from '@mui/material'
import { TextInputClassic } from '../inputs'
import { BorderButtonPurple, CustomButton } from '../buttons'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_errors.scss'
import { useTranslation } from 'react-i18next'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GradientButton from '../buttons/GradientButton'
import OutlineButton from '../buttons/OutlineButton'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 32px)',
  maxWidth: 800,
  minHeight: 400,
  maxHeight: '90vh',
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '16px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 800,
    padding: '20px 40px',
  },
}))

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}))

const InputContainer = styled(FormControl)(({ theme }) => ({
  rowGap: '5px',
}))

const EditPasswordModal = ({ open, onClose, id, onSuccess }) => {
  const { t } = useTranslation()

  const schema = yup
    .object({
      oldPassword: yup.string().required(t('errorMessage.oldPassword')),
      newPassword: yup.string().required(t('errorMessage.newPassword')),
      confirmNewPassword: yup
        .string()
        .required(t('errorMessage.confirmNewPassword'))
        .oneOf([yup.ref('newPassword')], t('errorMessage.passwordNotMatch')),
    })
    .required()
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: 'onChange' })

  const [loading, setLoading] = useState(false)
  /*  const [err, setErr] = useState(false)
  const [msg, setMsg] = useState('') */

  const onSubmit = async (data) => {
    setLoading(true)
    let result = await Api.auth.checkPassword({
      password: data.oldPassword,
      newPassword: data.newPassword,
    })

    const res = handlingErrors(result)
    if (!res.ok) {
      /* setMsg(res.message)
      setErr(true) */
      setLoading(false)
      window.scrollTo(0, 0)
    } else {
      if (!res.data) {
        setError(
          'oldPassword',
          { message: t('errorMessage.incorrectOldPassword') },
          { shouldFocus: true }
        )
      } else {
        const result = await Api.user.edit(id, { password: data.newPassword })
        const res = handlingErrors(result)
        if (!res.ok) {
          /*  setMsg(res.message)
          setErr(true) */
          window.scrollTo(0, 0)
        } else {
          reset()
          onClose()
          onSuccess()
        }
        // EDIT PASSWORD SEND TO API USER UPDATE
      }
      setLoading(false)
    }
    // reset();
    // onClose();
  }

  const fullScreen = useMediaQuery(lightTheme.breakpoints.down('sm'))
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      scroll={'paper'}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: fullScreen ? 0 : '8px',
          m: fullScreen ? 0 : undefined,
          maxHeight: fullScreen ? '100%' : '90vh',
          ...(fullScreen && {
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          pb: 2,
          flexShrink: 0,
          borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
          backgroundColor: 'rgba(249, 250, 251, 0.5)',
        }}
      >
        <Typography
          variant={fullScreen ? 'subtitle1' : 'h6'}
          sx={{ fontWeight: 600, color: '#1f2937', pr: 1, lineHeight: 1.3 }}
        >
          {t('box.title.editPassword')}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#6b7280',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.1)',
              color: '#374151',
            },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, pt: '16px !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '30px',
          }}
        >
          <form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputContainer fullWidth>
                  <FormLabel>{t('box.oldPassword')}</FormLabel>
                  <TextInputClassic
                    placeholder={t('box.oldPasswordText')}
                    type={'password'}
                    register={{ ...register('oldPassword') }}
                  />
                  {errors.oldPassword && (
                    <Typography className={'error-message'}>
                      {errors.oldPassword.message}
                    </Typography>
                  )}
                </InputContainer>
              </Grid>
              <Grid item xs={12}>
                <InputContainer fullWidth>
                  <FormLabel>{t('box.newPassword')}</FormLabel>
                  <TextInputClassic
                    placeholder={t('box.newPasswordText')}
                    type={'password'}
                    register={{ ...register('newPassword') }}
                  />
                  {errors.newPassword && (
                    <Typography className={'error-message'}>
                      {errors.newPassword.message}
                    </Typography>
                  )}
                </InputContainer>
              </Grid>
              <Grid item xs={12}>
                <InputContainer fullWidth>
                  <FormLabel>{t('box.confirmNewPassword')}</FormLabel>
                  <TextInputClassic
                    placeholder={t('box.confirmNewPasswordText')}
                    type={'password'}
                    register={{ ...register('confirmNewPassword') }}
                  />
                  {errors.confirmNewPassword && (
                    <Typography className={'error-message'}>
                      {errors.confirmNewPassword.message}
                    </Typography>
                  )}
                </InputContainer>
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 3,
          gap: 2,
          justifyContent: 'center',
          backgroundColor: 'rgba(249, 250, 251, 0.8)',
          borderTop: '1px solid rgba(229, 231, 235, 0.8)',
        }}
      >
        <OutlineButton onClick={onClose} fullWidth={fullScreen}>
          {t('button.cancel')}
        </OutlineButton>
        <GradientButton onClick={handleSubmit(onSubmit)} fullWidth={fullScreen} disabled={loading}>
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {t('profile.saving')}
            </>
          ) : (
            t('button.save')
          )}
        </GradientButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditPasswordModal
